import React, { useEffect, useState } from "react";
import {Stack, TextField, Button, Link} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import './EmailEntry.scss';
import { resendEmailVerify } from "../../../../slices/userSlice";

export default function EmailEntry({ timerKey = "emailVerificationResendExpiry" }) {
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [timeLeft, setTimeLeft] = useState("");
    const navigate = useNavigate();
    const RESEND_TIMER_MS = 10 * 60 * 1000;

    const validateEmail = (email) => {
        if (!email) return "Email is required";
        if (email.length > 255) return "Email must be at most 255 characters";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Invalid email format";
    }
    const [isBlocked, setIsBlocked] = useState(() => {
    const expiry = localStorage.getItem(timerKey);
            if (!expiry) return false;

            const hasNotExpired = Date.now() < parseInt(expiry, 10);
            if (!hasNotExpired) {
                localStorage.removeItem(timerKey);
            }

            return hasNotExpired;
        });

    const handleSubmit = async (event) => {
        event.preventDefault();

        const error = validateEmail(email);
        if (error) {
            setEmailError(error);
            return;
        }
        setEmailError('');

        const response = await dispatch(resendEmailVerify(email));

        if(resendEmailVerify.fulfilled.match(response)){
            if(window.location.hostname === "localhost") {
                console.log("Email verificationrequest successful:", response.payload);
            }
            
            // Navigates to success page with email in state for display
            // Will display whether email is associated with an account or not to prevent email enumeration
            // Fails to navigate to success page only if there is a network error or server error, otherwise will navigate to success page even if email is not associated with an account
            startTimer();
            navigate('/check-inbox', {
                state: {
                    userEmail: email,
                    header: "Verification Email Sent!"
                }
            });
        };
        // If the request was rejected, check if it's a server error or network error and display appropriate message. Otherwise, navigate to success page with generic message to prevent email enumeration.
        if(resendEmailVerify.rejected.match(response)){
            if(window.location.hostname === "localhost") {
                console.error("Email verification request failed:", response.payload);
            }
            
            if (typeof response.payload === "string") {
                toast.error("Network connection error. Please check your connection and try again.");
                return;
            }

            const { status, message, retryAfterSeconds } = response.payload || {};

            if (
                status === 429 ||
                (typeof message === "string" &&
                message.includes("Please wait before requesting another verification email"))
            ) {
                const milliseconds = retryAfterSeconds
                    ? retryAfterSeconds * 1000
                    : 10 * 60 * 1000;

                startTimer(milliseconds);
                toast.error("Please wait before trying again. You can only request a verification email once every 10 minutes.");
                    return;
            } else if(status === 503) {
                if(message === "SMTP_AUTH_FAIL") {
                    toast.error("The mail service is currently unavailable due to authentication issues. Please try again later.");
                } else {
                    toast.error("The mail service is currently unavailable. Please try again later.");
                }
                return;
            } else if(status === 500) {
                toast.error("Server Error. Network connection successful but the server encountered an error while processing the request. Please try again later.");
                return;
            } else if(status === 400) {
                toast.error("Invalid email format. Please enter a valid email address.");
                return;
            } else {
                toast.error("An unexpected error occurred. Please try again later.");
            }
        }
    };
      const getButtonText = () => {
        if (isBlocked) return `Please wait ${timeLeft} to try again.`
        return "Send Email";
    }
   const startTimer = (milliseconds = RESEND_TIMER_MS) => {
    const expiryTime = Date.now() + milliseconds;
    localStorage.setItem(timerKey, expiryTime);
    setIsBlocked(true);
        };

    useEffect(() => {
    if (!isBlocked) return;

    const calculateTimeLeft = () => {
        const expiry = localStorage.getItem(timerKey);
        if (!expiry) return null;

        const difference = parseInt(expiry, 10) - Date.now();
        if (difference <= 0) {
            return null;
        }

        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        return `${minutes}m ${seconds}s`;
    };

    const initialTime = calculateTimeLeft();
    if (initialTime) {
        setTimeLeft(initialTime);
    } else {
        setIsBlocked(false);
        localStorage.removeItem(timerKey);
    }

    const timer = setInterval(() => {
        const remaining = calculateTimeLeft();
        if (remaining) {
            setTimeLeft(remaining);
        } else {
            setIsBlocked(false);
            localStorage.removeItem(timerKey);
            clearInterval(timer);
        }
    }, 1000);

    return () => clearInterval(timer);
        }, [isBlocked, timerKey]);

    return (
        <div className="forgot-password-page" data-testid="forgot-password-page">
            <div className="forgot-password-wrapper" data-testid="forgot-password-wrapper">
                <div className="form-container" data-testid="form-container">
                    <Stack 
                    spacing={1}
                    component="form"
                    onSubmit={handleSubmit}
                    data-testid="forgot-password-form"
                    noValidate
                    >
                        <h1 className="WelcomeHeader" data-testid="welcome-header">Resend Email Verification</h1>
                        <p className="ResetInformation" data-testid="reset-information">Enter the email associated with your account and an email will be sent.</p>

                        <TextField
                            id="email"
                            data-testid="email-input"
                            name="email"
                            required
                            label="Email"
                            variant="filled"
                            size="small"
                            type="email"
                            fullWidth
                            value={email}
                            
                            onChange={(e) => {
                                const inputValue = e.target.value;
                                setEmail(inputValue);

                                if (inputValue.length > 255) {
                                setEmailError('Email cannot exceed 255 characters');
                                } else {
                                setEmailError('');
                                }
                            }}
                            error={!!emailError}
                            helperText={emailError}
                        />

                        <Button
                            variant="contained"
                            color="#51219D"
                            size="small"
                            type="submit"
                            fullWidth
                            data-testid="send-email-button"
                            disabled={isBlocked}
                            >
                                {getButtonText()}
                            </Button>
                        <h3 className="accountHeader" data-testid="account-header">Return to <Link href="/Login" data-testid="login-link">Login</Link></h3>
                    </Stack>
                </div>
            </div>
        </div>
    )
}